import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface RoundedRectIndicatorProps {
  /**
   * Width of the indicator in pixels
   */
  width?: number;
  /**
   * Height of the indicator in pixels (or string for variable height)
   */
  height?: number | string;
  /**
   * Minimum height in pixels (used when height is variable)
   */
  minHeight?: number;
  /**
   * Border radius for rounded corners (number in px or string like "50%" for circles)
   */
  borderRadius?: number | string;
  /**
   * Background color (MUI theme color path or hex)
   */
  bgcolor?: string;
  /**
   * Border color (MUI theme color path or hex)
   */
  borderColor?: string;
  /**
   * Background color on hover
   */
  hoverBgcolor?: string;
  /**
   * Border color on hover
   */
  hoverBorderColor?: string;
  /**
   * Width on hover (for expansion effect)
   */
  hoverWidth?: number;
  /**
   * Scale transform on hover
   */
  hoverScale?: number;
  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Mouse enter handler
   */
  onMouseEnter?: () => void;
  /**
   * Mouse leave handler
   */
  onMouseLeave?: () => void;
  /**
   * Whether the indicator is currently hovered (for controlled hover state)
   */
  isHovered?: boolean;
  /**
   * Additional sx props
   */
  sx?: SxProps<Theme>;
  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;
}

/**
 * Shared rounded rectangle indicator component for gutter indicators
 * Used by compliance rules, attestation edits, and bracket highlights
 */
export const RoundedRectIndicator: React.FC<RoundedRectIndicatorProps> = ({
  width = 12,
  height = 16,
  minHeight,
  borderRadius = 1.5,
  bgcolor,
  borderColor,
  hoverBgcolor,
  hoverBorderColor,
  hoverWidth,
  hoverScale = 1,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHovered = false,
  sx,
  'aria-label': ariaLabel,
}) => {
  return (
    <Box
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
      sx={{
        width: `${width}px`,
        height: typeof height === 'number' ? `${height}px` : height,
        minHeight: minHeight ? `${minHeight}px` : undefined,
        borderRadius,
        bgcolor: isHovered && hoverBgcolor ? hoverBgcolor : bgcolor,
        border: '1.5px solid',
        borderColor: isHovered && hoverBorderColor ? hoverBorderColor : borderColor,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        transform: isHovered && hoverScale !== 1 ? `scale(${hoverScale})` : 'scale(1)',
        pointerEvents: 'auto',
        '&:hover': {
          bgcolor: hoverBgcolor,
          borderColor: hoverBorderColor,
          width: hoverWidth ? `${hoverWidth}px` : undefined,
          transform: hoverScale !== 1 ? `scale(${hoverScale})` : undefined,
        },
        ...sx,
      }}
    />
  );
};

